import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LocationGroupScheduleComponentContextProps } from 'src/components/context/Context.interface';
import { InfoBox } from 'src/components/infoBox/InfoBox';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import {
  ILocation,
  ILocationGroupWithLocations,
  IMappedLocationGroupDaypart,
} from 'src/interfaces';
import { lang } from 'src/utils';

import { EXPANDED_ROW_COLUMNS } from './constants';
import { LinkContainer } from './ReviewPageContent.styles';

const AssemblyName = ({
  assemblyName,
  assemblyId,
}: {
  assemblyName: string;
  assemblyId: number;
}) => {
  return (
    <CustomTooltip
      width={500}
      placement="right"
      title={assemblyName}
      overflowWidth={334}
      renderChildren={(ref, style) => (
        <LinkContainer variant="text">
          <Link
            to={`${PATHS.ASSEMBLY_DETAILS(assemblyId)}`}
            target="_blank"
            ref={ref}
            style={style}>
            {assemblyName}
          </Link>
        </LinkContainer>
      )}
    />
  );
};

export const getSingleLocationGroupTableRows = (
  locationGroups: ILocationGroupWithLocations[],
  contexts: LocationGroupScheduleComponentContextProps,
  positionString: string,
  locationGroupDefaultAssemblies: IMappedLocationGroupDaypart[],
): {
  nsn: ReactNode;
  daypart: string;
  position: string;
  defaultAssembly: ReactNode;
}[] => {
  if (locationGroups[0]) {
    const assembly = locationGroupDefaultAssemblies
      .filter((item) => item.locationGroupId === locationGroups[0].id)
      .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

    const defaultAssembly = (
      <AssemblyName
        assemblyName={assembly?.assemblyData?.name || ''}
        assemblyId={assembly?.assemblyData?.id ?? 0}
      />
    );

    if (
      !locationGroups[0].locations ||
      locationGroups[0].locations.length === 0
    ) {
      return [
        {
          nsn: <InfoBox visible>{lang.noNsnsAssigned}</InfoBox>,
          daypart: contexts.daypartName,
          position: positionString,
          defaultAssembly,
        },
      ];
    }

    return locationGroups[0].locations.map((location) => ({
      nsn: location.store_number,
      daypart: contexts.daypartName,
      position: positionString,
      defaultAssembly,
    }));
  }

  return [];
};

export const getMultipleLocationGroupTableRows = (
  locationGroups: ILocationGroupWithLocations[],
  contexts: LocationGroupScheduleComponentContextProps,
  positionString: string,
  locationGroupDefaultAssemblies: IMappedLocationGroupDaypart[],
) => {
  return locationGroups.map((locationGroup) => {
    const assembly = locationGroupDefaultAssemblies
      .filter((item) => item.locationGroupId === locationGroup.id)
      .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

    const hasNsns = (locationGroup.locations?.length ?? 0) > 0;

    return {
      id: locationGroup.id,
      name: locationGroup.name,
      daypart: contexts.daypartName,
      defaultAssembly: (
        <AssemblyName
          assemblyName={assembly?.assemblyData.name || ''}
          assemblyId={assembly?.assemblyData.id ?? 0}
        />
      ),
      hasNsns,
      columns: EXPANDED_ROW_COLUMNS,
      rows: hasNsns
        ? getExpandedRows(positionString, locationGroup.locations)
        : [],
    };
  });
};

export const getExpandedRows = (
  positionString: string,
  locations?: ILocation[],
): { nsn: ReactNode; position: string }[] => {
  if (!locations || locations.length === 0) {
    return [
      {
        nsn: <InfoBox visible>{lang.noNsnsAssigned}</InfoBox>,
        position: positionString,
      },
    ];
  }

  return locations.map((location) => ({
    nsn: location.store_number,
    position: positionString,
  }));
};
